<template>
  <div class="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 relative">
    <!-- 🌀 Loader Overlay -->
    <div
      v-if="isLoading"
      class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50 rounded-2xl"
    >
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
    </div>

    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
      <h2 class="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <i class="ri-price-tag-3-line text-blue-600 text-2xl"></i> Plans List
      </h2>

      <button
        @click="openAddModal"
        class="mt-3 sm:mt-0 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm transition"
      >
        <i class="ri-add-line text-lg"></i> Add New Plan
      </button>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto border border-gray-100 rounded-xl">
      <table class="min-w-full text-left text-sm text-gray-700">
        <thead class="bg-gray-50 text-gray-600 text-sm uppercase font-medium">
          <tr>
            <th class="px-6 py-3">Plan Name</th>
            <th class="px-6 py-3">Type</th>
            <th class="px-6 py-3">Duration</th>
            <th class="px-6 py-3">Price</th>
            <th class="px-6 py-3">Client</th>
            <th class="px-6 py-3 text-center">Action</th>
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-100">
          <tr
            v-for="(plan, index) in paginatedPlans"
            :key="plan.id || index"
            class="hover:bg-blue-50/40 transition"
          >
            <td class="px-6 py-4 font-medium text-gray-900">{{ plan.name }}</td>
            <td class="px-6 py-4">{{ plan.type }}</td>
            <td class="px-6 py-4">{{ plan.duration }}</td>
            <td class="px-6 py-4">${{ plan.price }}</td>
            <td class="px-6 py-4">{{ plan.plan_client }}</td>
            <td class="px-6 py-4 text-center flex items-center justify-center gap-2">
              <button @click="openEditModal(plan.id)" class="text-blue-600 hover:text-blue-800">
                <i class="ri-edit-2-line"></i>
              </button>
              <button @click="deletePlan(plan.id)" class="text-red-500 hover:text-red-700">
                <i class="ri-delete-bin-6-line"></i>
              </button>
            </td>
          </tr>
          <tr v-if="paginatedPlans.length === 0">
            <td colspan="5" class="text-center text-gray-500 py-6">No plans available</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex items-center justify-between mt-6">
      <p class="text-sm text-gray-500">
        Showing <b>{{ startIndex + 1 }}</b>-<b>{{ endIndex }}</b> of <b>{{ totalRecords }}</b> plans
      </p>
      <div class="flex gap-2">
        <button
          @click="prevPage"
          :disabled="page === 1"
          class="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50"
        >
          <i class="ri-arrow-left-s-line"></i>
        </button>
        <button
          @click="nextPage"
          :disabled="endIndex >= totalRecords"
          class="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50"
        >
          <i class="ri-arrow-right-s-line"></i>
        </button>
      </div>
    </div>

   <!-- Add/Edit Modal -->
<transition name="fade">
  <div
    v-if="showModal"
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
  >
    <div
      class="bg-white rounded-2xl shadow-xl w-[90%] max-w-lg p-6 relative animate-popUp overflow-y-auto max-h-[90vh]"
    >
      <!-- Close Button -->
      <button
        @click="closeModal"
        class="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
      >
        <i class="ri-close-line text-2xl"></i>
      </button>

      <!-- Header -->
      <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <i class="ri-add-line text-blue-600"></i>
        {{ editMode ? "Edit Plan" : "Add New Plan" }}
      </h3>

      <!-- Messages -->
      <div
        v-if="msgTxt"
        :class="`p-3 mb-3 rounded-lg text-sm ${
          msgType === 'success'
            ? 'bg-green-100 text-green-700'
            : msgType === 'danger'
            ? 'bg-red-100 text-red-700'
            : 'bg-gray-100 text-gray-600'
        }`"
      >
        {{ msgTxt }}
      </div>

      <!-- Form -->
      <form @submit.prevent="submitPlan" class="space-y-4">
        <!-- Image -->
       

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
           <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Plan Image</label>
            <input type="file" @change="set_img" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Website Limit</label>
            <input
              v-model="form.plan_client"
              type="number"
              required
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
        </div>

        <!-- Title & Type -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
            <input
              v-model="form.plan_title"
              required
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select v-model="form.plan_type" class="w-full border border-gray-300 rounded-lg px-3 py-2">
              <option value="Month">Month</option>
              <option value="Year">Year</option>
            </select>
          </div>
        </div>

        <!-- Price & Duration -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
            <input
              v-model="form.plan_price"
              type="number"
              required
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Duration (Days)</label>
            <input
              v-model="form.plan_duration"
              type="number"
              required
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
        </div>

        <!-- Features (Scrollable) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Features</label>
          <div
            class="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50/30 custom-scroll"
          >
            <div
              v-for="(f, i) in form.plan_features"
              :key="i"
              class="flex gap-2 mb-2 items-center"
            >
              <input
                v-model="f.title"
                type="text"
                placeholder="Enter feature"
                class="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <button
                type="button"
                @click="removeFeature(i)"
                class="text-red-500 hover:text-red-700"
              >
                <i class="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>
          <button
            type="button"
            @click="addFeature"
            class="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
          >
            <i class="ri-add-line"></i> Add Feature
          </button>
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            v-model="form.plan_desc"
            rows="3"
            class="w-full border border-gray-300 rounded-lg px-3 py-2"
          ></textarea>
        </div>

        <!-- Buttons -->
        <div class="flex justify-end gap-3 pt-3">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {{ editMode ? "Update Plan" : "Save Plan" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</transition>

  </div>
</template>

<script>
import {
  getPlanList,
  addPlan,
  getPlanById,
  deletePlanById,
} from "@/apis/memplanapis";
import { uploadImg } from "@/apis/bookingpageapis";

export default {
  name: "PlansList",
  data() {
    return {
      page: 1,
      perPage: 5,
      totalRecords: 0,
      plans: [],
      showModal: false,
      editMode: false,
      isLoading: false,
      popupLoading: false,
      msgTxt: "",
      msgType: "",
      form: {
        plan_id: "",
        plan_title: "",
        plan_type: "Month",
        plan_price: "",
        plan_duration: "",
        plan_desc: "",
        plan_client: "",
        plan_img: "",
        plan_features: [{ title: "", status: true }],
      },
      ldata: null,
    };
  },
  computed: {
    startIndex() {
      return (this.page - 1) * this.perPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.perPage, this.totalRecords);
    },
    paginatedPlans() {
      return this.plans.slice(this.startIndex, this.endIndex);
    },
  },
  methods: {
    isJson(str) {
      if (typeof str !== "string") return false;
      try {
        JSON.parse(str);
        return true;
      } catch (e) {
        return false;
      }
    },
    async fetchPlans() {
      try {
        this.isLoading = true;
        const res = await getPlanList(this.page - 1, this.perPage, this.ldata.user_id);
        if (res.status) {
          this.plans = res.records.map((p) => ({
            id: p.plan_id,
            name: p.plan_title,
            type: p.plan_type,
            duration: p.plan_duration + " Days",
            plan_client:p.plan_client,
            price: p.plan_price,
          }));
          this.totalRecords = parseInt(res.total1 || this.plans.length);
        }
      } finally {
        this.isLoading = false;
      }
    },

    openAddModal() {
      this.editMode = false;
      this.msgTxt = "";
      this.form = {
        plan_id: "",
        plan_title: "",
        plan_type: "Month",
        plan_price: "",
        plan_duration: "",
        plan_client:"",
        plan_desc: "",
        plan_img: "",
        plan_features: [{ title: "", status: true }],
      };
      this.showModal = true;
    },

    async openEditModal(planId) {
      this.editMode = true;
      this.showModal = true;
      this.msgTxt = "";
      this.popupLoading = true;
      try {
        const res = await getPlanById(planId);
        if (res.status) {
          const p = res.plan_dt;
          this.form = {
            plan_id: p.plan_id,
            plan_title: p.plan_title,
            plan_type: p.plan_type,
            plan_price: p.plan_price,
            plan_duration: p.plan_duration,
            plan_client:p.plan_client,
            plan_desc: p.plan_desc,
            plan_img: p.plan_img || "",
           plan_features: (p.plan_features && this.isJson(p.plan_features))
  ? JSON.parse(p.plan_features)
  : [{ title: "", status: true }],

          };
        }
      } finally {
        this.popupLoading = false;
      }
    },

    async set_img(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.popupLoading = true;
      try {
        const res = await uploadImg(file);
        if (res.status) {
          this.form.plan_img = res.data;
          this.msgType = "success";
          this.msgTxt = "Image uploaded successfully!";
        } else {
          this.msgType = "danger";
          this.msgTxt = "Image upload failed.";
        }
      } finally {
        this.popupLoading = false;
      }
    },

    async submitPlan() {
      this.msgTxt = "";
      this.msgType = "";
      this.popupLoading = true;
      try {
        const res = await addPlan(
          this.form.plan_id,
          this.form.plan_title,
          this.form.plan_price,

          this.form.plan_duration,
          this.form.plan_desc,
          this.form.plan_img,

          this.form.plan_type,
          this.form.plan_client,
          "",
          "INR",
          JSON.stringify(this.form.plan_features)
        );
        if (res.status) {
          this.msgType = "success";
          this.msgTxt = res.message || "Plan saved successfully!";
          this.fetchPlans();
          setTimeout(() => this.closeModal(), 1000);
        } else {
          this.msgType = "danger";
          this.msgTxt = res.message || "Failed to save plan.";
        }
      } finally {
        this.popupLoading = false;
      }
    },

    async deletePlan(id) {
      this.isLoading = true;
      try {
        const res = await deletePlanById(id);
        if (res.status) {
          this.msgType = "success";
          this.msgTxt = "Plan deleted successfully!";
          this.fetchPlans();
        }
      } finally {
        this.isLoading = false;
      }
    },

    addFeature() {
      this.form.plan_features.push({ title: "", status: true });
    },
    removeFeature(i) {
      if (this.form.plan_features.length > 1)
        this.form.plan_features.splice(i, 1);
    },
    closeModal() {
      this.showModal = false;
    },
    nextPage() {
      if (this.endIndex < this.totalRecords) {
        this.page++;
        this.fetchPlans();
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page--;
        this.fetchPlans();
      }
    },
  },
  async created() {
    const ldataRaw = sessionStorage.getItem("ldata");
    this.ldata = ldataRaw ? JSON.parse(ldataRaw) : { user_id: 0 };
    this.fetchPlans();
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes popUp {
  from {
    transform: scale(0.95);
    opacity: 0.5;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
.animate-popUp {
  animation: popUp 0.3s ease-out;
}
table th {
  background-color: #f9fafb;
  font-weight: 600;
  letter-spacing: 0.5px;
}
table td {
  vertical-align: middle;
}
</style>
